import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Breadcrumbs,
  Container,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Modal,
  Paper,
  Typography,
} from '@mui/material';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ExpandMore as ExpandMoreIcon, Menu as MenuIcon, MenuOpen as MenuOpenIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  getAcademyBaseUrl,
  loadAcademyLessonMarkdown,
  loadAcademyManifest,
  replaceDeeplinkShortcodes,
  resolveAcademyAssetUrl,
} from './academyContent';

const AcademyLessonPage = () => {
  const { slug } = useParams();
  const location = useLocation();
  const academyBaseUrl = useMemo(() => getAcademyBaseUrl(), [location.search]);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState(null);
  const [markdown, setMarkdown] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [expandedImage, setExpandedImage] = useState(null);
  const [manifest, setManifest] = useState(null);
  const [manifestError, setManifestError] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [expandedTrack, setExpandedTrack] = useState(null);

  useEffect(() => {
    let isMounted = true;
    setStatus('loading');
    setError(null);
    setNotFound(false);
    setExpandedImage(null);

    loadAcademyLessonMarkdown(academyBaseUrl, slug)
      .then((result) => {
        if (!isMounted) {
          return;
        }
        if (result?.notFound) {
          setNotFound(true);
          setStatus('loaded');
          return;
        }
        setMarkdown(replaceDeeplinkShortcodes(result?.markdown || ''));
        setStatus('loaded');
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setError(err.message || 'Unable to load the lesson.');
        setStatus('error');
      });

    return () => {
      isMounted = false;
    };
  }, [academyBaseUrl, slug]);

  useEffect(() => {
    let isMounted = true;
    setManifestError(null);

    loadAcademyManifest(academyBaseUrl)
      .then((data) => {
        if (!isMounted) {
          return;
        }
        setManifest(data);
      })
      .catch((err) => {
        if (!isMounted) {
          return;
        }
        setManifestError(err.message || 'Unable to load Academy sections.');
      });

    return () => {
      isMounted = false;
    };
  }, [academyBaseUrl]);

  const resolvedMarkdown = markdown || '';
  const handleImageOpen = (src, alt) => {
    setExpandedImage({ src, alt });
  };

  const handleImageClose = () => {
    setExpandedImage(null);
  };

  const renderImageButton = ({ src, alt, ...props }) => (
    <button
      type="button"
      className="academy-image-button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        handleImageOpen(src, alt);
      }}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="academy-image"
        {...props}
      />
    </button>
  );

  const tracks = manifest?.tracks || [];
  const { currentTrack, currentLesson } = useMemo(() => {
    for (const track of tracks) {
      for (const lesson of track.lessons || []) {
        if (lesson.slug === slug) {
          return { currentTrack: track, currentLesson: lesson };
        }
      }
    }
    return { currentTrack: null, currentLesson: null };
  }, [tracks, slug]);

  useEffect(() => {
    if (currentTrack?.id) {
      setExpandedTrack(currentTrack.id);
    }
  }, [currentTrack?.id]);

  const lessonTitle = currentLesson?.title || slug;
  const breadcrumbTrackLabel = currentTrack?.title || 'Lessons';
  const breadcrumbTrackHref = currentTrack?.id ? `/learn#${currentTrack.id}` : '/learn';

  return (
    <Container maxWidth="lg" sx={{ py: 5, px: { xs: 0, sm: 3, md: 4 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
          {lessonTitle}
        </Typography>
        <Breadcrumbs aria-label="academy breadcrumbs">
          <Typography component={Link} to="/learn" sx={{ fontWeight: 600 }}>
            Learn
          </Typography>
          <Typography component={Link} to={breadcrumbTrackHref} sx={{ fontWeight: 600 }}>
            {breadcrumbTrackLabel}
          </Typography>
          <Typography color="text.secondary">{lessonTitle}</Typography>
        </Breadcrumbs>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
          {currentTrack?.description || ''}
        </Typography>
        <IconButton
          aria-label={sidebarOpen ? 'Collapse lesson sidebar' : 'Expand lesson sidebar'}
          onClick={() => setSidebarOpen((open) => !open)}
          size="small"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            display: { xs: 'none', md: 'inline-flex' },
          }}
        >
          {sidebarOpen ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />}
        </IconButton>
      </Box>

      {status === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {status === 'loading' && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="body1">Loading lesson...</Typography>
        </Paper>
      )}

      {status === 'loaded' && notFound && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Lesson not found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            The requested lesson does not exist yet. Head back to the Academy homepage.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Typography component={Link} to="/learn" sx={{ fontWeight: 600 }}>
              Return to Academy
            </Typography>
          </Box>
        </Paper>
      )}

      {manifestError && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          {manifestError}
        </Alert>
      )}

      {status === 'loaded' && !notFound && (
        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
          {sidebarOpen && (
            <Paper
              sx={{
                p: 2,
                width: { xs: '100%', md: 280 },
                display: { xs: 'none', md: 'block' },
                flexShrink: 0,
                position: { md: 'sticky' },
                top: { md: 96 },
                alignSelf: { md: 'flex-start' },
              }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                Lesson Map
              </Typography>
              {tracks.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  Lesson sections will appear once the manifest loads.
                </Typography>
              )}
              {tracks.map((track) => (
                <Accordion
                  key={track.id}
                  disableGutters
                  elevation={0}
                  expanded={expandedTrack === track.id}
                  onChange={(_, isExpanded) => setExpandedTrack(isExpanded ? track.id : null)}
                  sx={{ backgroundColor: 'transparent' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon fontSize="small" />}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {track.title}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0 }}>
                    <List disablePadding>
                      {track.lessons?.map((lesson) => {
                        const isActive = lesson.slug === slug;
                        return (
                          <ListItemButton
                            key={lesson.slug}
                            component={Link}
                            to={`/learn/${lesson.slug}`}
                            selected={isActive}
                            sx={{
                              borderRadius: 2,
                              mb: 0.5,
                              '&.Mui-selected': {
                                backgroundColor: 'rgba(0, 212, 255, 0.12)',
                              },
                            }}
                          >
                            <ListItemText
                              primary={lesson.title}
                              secondary={lesson.estimatedMinutes ? `${lesson.estimatedMinutes} min` : null}
                              primaryTypographyProps={{ fontWeight: isActive ? 700 : 500, variant: 'body2' }}
                              secondaryTypographyProps={{ variant: 'caption' }}
                            />
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Paper>
          )}

          <Paper sx={{ p: { xs: 3, md: 4 }, flex: 1, borderRadius: { xs: 0, md: 2 } }}>
            <Box className="academy-markdown">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  img: ({ src, alt, node: _node, ...props }) => {
                    const resolvedSrc = resolveAcademyAssetUrl({ baseUrl: academyBaseUrl, slug, src });
                    return renderImageButton({ src: resolvedSrc, alt, ...props });
                  },
                  a: ({ href, children, node }) => {
                    if (!href) {
                      return <span>{children}</span>;
                    }

                    const imageNode = node?.children?.find(
                      (child) => child?.tagName === 'img' || child?.type === 'image'
                    );
                    if (imageNode?.properties?.src) {
                      const linkContent = (
                        <span className="academy-linked-image__link-text">Open source</span>
                      );

                      return (
                        <div className="academy-linked-image">
                          <div className="academy-linked-image__content">
                            {children}
                          </div>
                          {href.startsWith('/') && !href.startsWith('//') ? (
                            <Link className="academy-linked-image__link" to={href}>
                              {linkContent}
                            </Link>
                          ) : (
                            <a className="academy-linked-image__link" href={href} target="_blank" rel="noreferrer">
                              {linkContent}
                            </a>
                          )}
                        </div>
                      );
                    }

                    if (href.startsWith('/') && !href.startsWith('//')) {
                      return (
                        <Link to={href}>
                          {children}
                        </Link>
                      );
                    }

                    return (
                      <a href={href}>
                        {children}
                      </a>
                    );
                  },
                }}
              >
                {resolvedMarkdown}
              </ReactMarkdown>
            </Box>
            <Modal
              open={Boolean(expandedImage)}
              onClose={handleImageClose}
              BackdropProps={{ onClick: handleImageClose }}
            >
              <Box className="academy-image-modal" onClick={handleImageClose}>
                <Box className="academy-image-modal__content" onClick={(event) => event.stopPropagation()}>
                  {expandedImage && (
                    <img
                      src={expandedImage.src}
                      alt={expandedImage.alt}
                      className="academy-image-modal__img"
                    />
                  )}
                </Box>
              </Box>
            </Modal>
          </Paper>
        </Box>
      )}
    </Container>
  );
};

export default AcademyLessonPage;
